import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { X, Share, Plus } from "lucide-react";

/*
 * InstallPrompt — Mobile PWA Batch B2
 *
 * Renders a chairside-friendly install banner on mobile viewports:
 *
 *   Android / Chrome / Edge: listens for `beforeinstallprompt`, shows an
 *   "Install" call-to-action, calls the native `prompt()` on tap.
 *
 *   iPhone Safari: no programmatic install exists — WebKit still requires
 *   the manual "Share → Add to Home Screen" gesture. This component
 *   detects iOS Safari (non-standalone) and shows a one-step visual hint.
 *
 * Suppression rules:
 *   • Already installed / running as standalone → never show.
 *   • User dismissed → suppressed for 7 days via localStorage.
 *   • Non-mobile viewports (≥ 768px) → never show (desktop uses the
 *     browser's own install icon).
 *
 * The banner is metadata-only. NO PHI, no user identifiers, no telemetry.
 */

const DISMISS_KEY = "pwa:install:dismissed_until";
const DISMISS_TTL_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

function isStandalone() {
  if (typeof window === "undefined") return false;
  const mq = window.matchMedia && window.matchMedia("(display-mode: standalone)");
  return (mq && mq.matches) || window.navigator.standalone === true;
}

function detectIOS() {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  // iPad on iOS 13+ reports as Mac; catch that via touch support.
  const iPadOS = /Macintosh/.test(ua) && "ontouchend" in document;
  return /iPad|iPhone|iPod/.test(ua) || iPadOS;
}

function isMobileViewport() {
  if (typeof window === "undefined") return false;
  return window.matchMedia("(max-width: 767px)").matches;
}

function isDismissedRecently() {
  try {
    const until = parseInt(localStorage.getItem(DISMISS_KEY) || "0", 10);
    return Number.isFinite(until) && Date.now() < until;
  } catch (_) {
    return false;
  }
}

export default function InstallPrompt() {
  const location = useLocation();
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showAndroid, setShowAndroid] = useState(false);
  const [showIOS, setShowIOS] = useState(false);
  const [hidden, setHidden] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    if (isStandalone()) return undefined;
    if (isDismissedRecently()) return undefined;
    if (!isMobileViewport()) return undefined;

    // Android / Chrome / Edge — real install prompt.
    const onBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setShowAndroid(true);
    };
    window.addEventListener("beforeinstallprompt", onBeforeInstall);

    const onInstalled = () => {
      setShowAndroid(false);
      setShowIOS(false);
      setDeferredPrompt(null);
    };
    window.addEventListener("appinstalled", onInstalled);

    // iOS Safari — no programmatic prompt exists. Show a manual hint.
    // Delay slightly so it doesn't fight the initial page paint.
    let iosTimer = null;
    if (detectIOS()) {
      iosTimer = window.setTimeout(() => setShowIOS(true), 2500);
    }

    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      window.removeEventListener("appinstalled", onInstalled);
      if (iosTimer) window.clearTimeout(iosTimer);
    };
  }, []);

  const dismiss = () => {
    setHidden(true);
    setShowAndroid(false);
    setShowIOS(false);
    try {
      localStorage.setItem(DISMISS_KEY, String(Date.now() + DISMISS_TTL_MS));
    } catch (_) {
      /* storage disabled — banner just won't persist dismissal */
    }
  };

  const install = async () => {
    if (!deferredPrompt) return;
    try {
      await deferredPrompt.prompt();
      await deferredPrompt.userChoice;
    } catch (_) {
      /* user cancelled or browser rejected — no-op */
    }
    setDeferredPrompt(null);
    setShowAndroid(false);
  };

  if (hidden) return null;
  // Mobile ARIA follow-up correction (R4/R6, 2026-08-15) — never render
  // the fixed install banner on Quick Assist. The banner sat at
  // z-40 above the typed input / action row and intercepted taps on the
  // page's primary controls. Behaviour on every other route is
  // unchanged: 7-day dismiss cool-down, iOS + Android surfaces intact.
  if (location && location.pathname && location.pathname.startsWith("/quick-assist")) {
    return null;
  }
  if (!showAndroid && !showIOS) return null;

  return (
    <div
      data-testid="pwa-install-banner"
      role="dialog"
      aria-label="Install ProsthoAI"
      className="fixed left-3 right-3 z-40 rounded-lg border border-[var(--pa-gold)]/40 bg-[var(--pa-bg-2)] shadow-2xl shadow-black/50 backdrop-blur-sm p-3"
      style={{
        bottom: "calc(env(safe-area-inset-bottom, 0px) + 96px)",
        animation: "installIn 240ms ease-out",
      }}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-widest text-[var(--pa-gold)]">
            {showAndroid ? "Install ProsthoAI" : "Add to Home Screen"}
          </div>
          {showAndroid ? (
            <p className="mt-1 text-sm text-[var(--pa-text)] leading-snug">
              Install ProsthoAI for one-tap chairside access — Ask the Bench even opens offline.
            </p>
          ) : (
            <p className="mt-1 text-sm text-[var(--pa-text)] leading-snug">
              Tap
              <Share
                size={14}
                className="inline-block mx-1 -mt-0.5 text-[var(--pa-gold)]"
                aria-label="Share"
              />
              then
              <span className="mx-1 inline-flex items-center gap-0.5 text-[var(--pa-gold)]">
                <Plus size={12} strokeWidth={2.5} />
                <span className="font-medium">Add to Home Screen</span>
              </span>
              to pin ProsthoAI to your iPhone.
            </p>
          )}
        </div>
        <button
          type="button"
          onClick={dismiss}
          data-testid="pwa-install-dismiss"
          aria-label="Dismiss install prompt"
          className="shrink-0 -mt-1 -mr-1 p-2 min-h-[44px] min-w-[44px] flex items-center justify-center text-[var(--pa-text-muted)] hover:text-[var(--pa-text)]"
        >
          <X size={16} />
        </button>
      </div>
      {showAndroid && (
        <div className="mt-3 grid grid-cols-2 gap-2">
          <button
            type="button"
            onClick={dismiss}
            data-testid="pwa-install-later"
            className="text-xs font-medium min-h-[44px] rounded-md border border-[var(--pa-border)] text-[var(--pa-text-muted)] hover:text-[var(--pa-text)] hover:border-[var(--pa-text-muted)] transition"
          >
            Not now
          </button>
          <button
            type="button"
            onClick={install}
            data-testid="pwa-install-btn"
            className="text-xs font-semibold min-h-[44px] rounded-md bg-[var(--pa-gold)] text-[var(--pa-bg)] hover:bg-[var(--pa-gold-hover)] transition"
          >
            Install
          </button>
        </div>
      )}
      <style>{`
        @keyframes installIn {
          0% { opacity: 0; transform: translateY(8px); }
          100% { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
}
